/**
 * Feature flags for work that ships in the codebase but is not finished.
 *
 * A flag is `false` when the UI exists and looks real but the thing behind it
 * does not work — no API, no schema, mocked data, or a button that goes
 * nowhere. Keeping the code but gating its entry points means the feature can
 * keep being developed without a user ever running into the broken half.
 *
 * Gate at the entry point (route, nav item, menu entry, toolbar tool), not
 * inside every leaf component. A hidden route redirects instead of rendering,
 * so a pasted URL cannot reach it either.
 *
 * Flip a flag to `true` only when the "turn on when" note below is satisfied.
 */

export type FeatureFlag =
  | 'SHARING'
  | 'MULTIPLE_WORKSPACES'
  | 'CANVAS_ASK_GENIE'
  | 'SETTINGS_PROFILE'
  | 'SETTINGS_SECURITY'
  | 'SETTINGS_DEVELOPER'
  | 'NODE_3D'

export const FEATURES: Record<FeatureFlag, boolean> = {
  /**
   * Sharing a session / workspace and the "Shared with me" surface.
   * Off: there is no sharing, membership or permission model in the database
   * at all — the share dialog's link points at a workspace id of "1" and its
   * Send button has no handler.
   * Turn on when: a sharing model exists in Prisma, the API can grant and
   * revoke access, and the share dialog actually calls it.
   */
  SHARING: false,

  /**
   * Multiple workspaces and the workspace switcher.
   * Off: workspaces live in one `useState` seeded with a hardcoded id of "1";
   * creating one writes to that local array and it disappears on reload.
   * The single implicit workspace (everything under /workspace) keeps working.
   * Turn on when: workspaces are rows the API owns and /workspace/$slug can
   * resolve a real one.
   */
  MULTIPLE_WORKSPACES: false,

  /**
   * Ask Genie panel on the canvas.
   * Off: the only implementation is `useMockAskGenie`, which replays scripted
   * text through setTimeout. There is no agent endpoint behind it.
   * Turn on when: the panel talks to a real Ask Genie API and its plan can be
   * applied to the canvas.
   */
  CANVAS_ASK_GENIE: false,

  /**
   * Settings › Profile.
   * Off: username and display name are hardcoded strings, the avatar is
   * /placeholder-avatar.jpg, and both buttons are TODO stubs — editing the
   * fields changes nothing on the account.
   * Turn on when: the section loads the session user and saves through the API.
   */
  SETTINGS_PROFILE: false,

  /**
   * Settings › Security.
   * Off: the email shown is a hardcoded sample address, Change email is a TODO,
   * and the change-password dialog validates the form then closes without
   * calling anything. 2FA has no backing table.
   * Turn on when: email change and password change go through Better Auth and
   * the two-factor tables exist.
   */
  SETTINGS_SECURITY: false,

  /**
   * Settings › Developer.
   * Off: the whole section is a dashed box that says "coming soon".
   * Turn on when: API keys or developer tokens exist.
   */
  SETTINGS_DEVELOPER: false,

  /**
   * 3D generation node.
   * Off: the node's Generate button dispatches a `genie:generate-node` event
   * that nothing listens to, and the progress bar is synthesised from a timer,
   * so it advances for a job that was never submitted.
   * Turn on when: 3D generation is wired to the generation API the way the
   * image and video nodes are.
   */
  NODE_3D: false,
}

/** Reads a flag. Prefer this over touching FEATURES directly in components. */
export function isFeatureEnabled(flag: FeatureFlag): boolean {
  return FEATURES[flag]
}
